import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Heart, Calendar, Sprout, Play, Maximize2, Volume2, VolumeX, Download } from 'lucide-react';
import { mockData } from '../mock';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import infographieImg from '../assets/infographie.jpg';

// =========================================================================
// CHARGEMENT AUTOMATIQUE DU CARROUSEL
// Scanne le dossier src/assets/carrousel
// =========================================================================
const importCarouselImages = () => {
  try {
    // Regex insensible à la casse (flag 'i') pour jpg, JPG, png, PNG, etc.
    const context = require.context('../assets/carrousel', false, /\.(png|jpe?g|svg|webp)$/i);
    return context.keys().map(context);
  } catch (err) {
    console.warn("Dossier 'assets/carrousel' introuvable ou vide.", err);
    return [];
  }
};

// Composant Vidéo Bandeau pour le Hero - Lecture inline
const HeroVideoBanner = ({ videoUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef(null);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);    
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);
  if (!videoId) return null;

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleFullscreen = () => {
    if (iframeRef.current) {
      if (iframeRef.current.requestFullscreen) {
        iframeRef.current.requestFullscreen();
      } else if (iframeRef.current.webkitRequestFullscreen) {
        iframeRef.current.webkitRequestFullscreen();
      } else if (iframeRef.current.msRequestFullscreen) {
        iframeRef.current.msRequestFullscreen();
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <div className="w-full my-6 md:my-8">
      <div 
        className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 backdrop-blur-sm"
        style={{ aspectRatio: '16/9' }}
      >
        {!isPlaying ? (
          <>
            <img
              src={thumbnailUrl}
              alt="Vidéo de présentation"
              className="w-full h-full object-cover"
            />
            <div 
              className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60 hover:from-black/50 hover:via-black/30 hover:to-black/50 transition-all duration-300 cursor-pointer"
              onClick={handlePlay}
            />
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center group"
              aria-label="Lire la vidéo"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white/90 rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                <Play size={32} className="text-black ml-1" fill="currentColor" />
              </div>
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-black/80 to-transparent">
              <p className="text-white/90 text-xs md:text-sm font-medium tracking-wide flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Découvrez notre univers en vidéo
              </p>
            </div>
          </>
        ) : (
          <>
            <iframe
              ref={iframeRef}
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1`}
              title="Vidéo de présentation Soul'Kitchen and Co."
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            ></iframe>
            <div className="absolute bottom-3 right-3 flex gap-2">
              <button
                onClick={toggleMute}
                className="bg-black/70 hover:bg-black/90 text-white p-2 md:p-2.5 rounded-full transition-colors shadow-lg"
                title={isMuted ? "Activer le son" : "Couper le son"}
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <button
                onClick={handleFullscreen}
                className="bg-black/70 hover:bg-black/90 text-white p-2 md:p-2.5 rounded-full transition-colors shadow-lg"
                title="Plein écran"
              >
                <Maximize2 size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Home = () => {
  const hasVideo = mockData.hero.videoUrl;
  
  // CHARGEMENT DES IMAGES DU CARROUSEL
  const carouselImages = importCarouselImages();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={mockData.hero.image}
            alt="Soul'Kitchen & Co."
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white py-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6">
            {mockData.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl mb-3 md:mb-4 font-bold">
            {mockData.hero.subtitle}
          </p>
          <p className="text-base md:text-lg lg:text-xl mb-2 max-w-2xl">
            {mockData.hero.description}
          </p>
          
          {/* Vidéo Bandeau (optionnelle) */}
         {/* hasVideo && <HeroVideoBanner videoUrl={mockData.hero.videoUrl} />*/}

        {/* DÉBUT DU CARROUSEL AUTOMATIQUE */}
        {/* MODIFICATION ICI : On limite la hauteur pour un effet 16/9ème (45vh au lieu de 70vh) */}
        <div className="carousel-wrapper relative w-full mt-10 rounded-xl overflow-hidden shadow-2xl" style={{ height: '45vh', minHeight: '300px' }}>
            <Carousel
              autoPlay={true}
              interval={4000}
              transitionTime={1200}
              infiniteLoop={true}
              showArrows={true}
              showThumbs={false}
              showStatus={false}
              showIndicators={false}
              stopOnHover={false}
              swipeable={true}
              emulateTouch={true}
              dynamicHeight={false} // Important pour forcer notre hauteur
            >
                {/* Boucle sur les images trouvées dans le dossier */}
                {carouselImages.length > 0 ? (
                  carouselImages.map((imgSrc, index) => (
                    // On applique la hauteur fixe ici aussi
                    <div key={index} style={{ height: '45vh', minHeight: '300px' }}>
                        <img 
                          src={imgSrc} 
                          alt={`Ambiance Soul Kitchen ${index + 1}`} 
                          // object-center permet de centrer l'image si elle est rognée
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} 
                        />
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-center bg-gray-200 text-gray-500 h-full">
                    <p>Ajoutez des images dans src/assets/carrousel</p>
                  </div>
                )}
            </Carousel>
        </div>
        {/* FIN DU CARROUSEL */}
          
          {/* Boutons CTA */}
          <div className={`flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 ${!hasVideo ? 'mt-6 md:mt-8' : ''}`}>
            <Link to="/contact" className="btn-primary text-center">
              Demander un devis
              <ArrowRight className="ml-2 inline-block" size={20} />
            </Link>
            <Link to="/services" className="btn-secondary-light text-center">
              Découvrir nos services
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-4 md:py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <h2 
            className="section-title text-center mb-10 md:mb-16 text-4xl md:text-6xl" 
            style={{ color: '#3E332E' }}
          >
            Nos Valeurs
          </h2> 

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {mockData.values.map((value, index) => {
              const icons = [Leaf, Sprout, Calendar, Heart];
              const Icon = icons[index];
              return (
                <div key={value.id} className="value-card bg-primary-dark p-6 md:p-8 rounded-lg transition-transform hover:transform hover:-translate-y-2">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-green rounded-full flex items-center justify-center mb-4 md:mb-6">
                    <Icon className="text-[#F9F6F0]" size={24} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4" style={{ color: '#3E332E' }}>
                    {value.title}
                  </h3>
                  <p className="text-sm md:text-base text-[#3E332E]">{value.description}</p>
                </div>
              );
            })} 
          </div>

          <div className="mt-12 md:mt-16 w-full hidden md:block">
             <img 
               src={infographieImg} 
               alt="Infographie Soul Kitchen : Notre vision et nos engagements" 
               className="w-full h-auto rounded-xl shadow-lg"
             />
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title mb-4 md:mb-6 text-3xl md:text-6xl">Prêt à transformer votre restauration collective ?</h2>
          <p className="text-lg md:text-xl text-[#3E332E] mb-6 md:mb-8">
            Contactez-nous pour échanger sur vos besoins et voir comment nous pouvons vous accompagner dans la création d’une belle expérience culinaire.
          </p>
          <Link to="/contact" className="btn-primary hover:!bg-[#DDBA6B] transition-colors inline-flex items-center justify-center min-w-[230px]">
            Nous contacter
            <ArrowRight className="ml-2 inline-block" size={20} />
          </Link>

          {/* NOUVEAU BLOC FLYER */}
          <div className="mt-12 max-w-4xl mx-auto">
            <p className="text-lg md:text-xl text-[#3E332E] mb-6 md:mb-8">
              Télécharger notre flyer pour en savoir plus sur l'esprit de Soul'Kitchen and Co.
            </p>
            <a 
              href="/images/Soul kitchen and co - Flyer.pdf" 
              download="Soul kitchen and co - Flyer.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary hover:!bg-[#DDBA6B] transition-colors inline-flex items-center justify-center min-w-[230px]"
            >
              Telecharger le flyer
              <Download className="ml-2 inline-block" size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;